import React, { useEffect, useState, useContext } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { AuthContext } from "../../context/AuthContext";
import { fetchWithAuth } from "../../utils/api";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;
  if (!processed.includes("$") && !processed.includes("\\(") && !processed.includes("\\[")) {
    const words = [];
    let current = "";
    let depth = 0;
    for (let i = 0; i < processed.length; i++) {
      const c = processed[i];
      if (c === "{" || c === "(") depth++;
      else if (c === "}" || c === ")") depth = Math.max(0, depth - 1);
      if (c === " " && depth === 0) {
        if (current) words.push(current);
        current = "";
      } else {
        current += c;
      }
    }
    if (current) words.push(current);
    processed = words.map((word) => (/(\\[a-zA-Z]+|\^|_)/.test(word) ? `$${word}$` : word)).join(" ");
  }
  processed = processed.replace(/(^|[^\\])\$([^$]+)\$/g, (match, before, math) => {
    if (math.includes("\\displaystyle")) return match;
    return `${before}$\\displaystyle ${math}$`;
  });
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes("\\displaystyle")) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });
  return processed;
};

const SPAN_COMPONENTS = { p: "span" };
const REMARK_PLUGINS = [remarkMath, remarkGfm, remarkBreaks];
const REHYPE_PLUGINS = [
  [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
];

const PerformanceReport = ({
  summary,
  isSummaryEditing,
  onSaveSummary,
  handleSaveSummary,
  instituteInfo: instituteInfoProp,
  classInfo: classInfoProp,
}) => {
  const [localSummary, setLocalSummary] = useState({
    ...summary,
    strengths: summary.strengths || [],
    weaknesses: summary.weaknesses || [],
    recommendations: summary.recommendations || [],
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
  });

  const {
    studentName,
    testName,
    classId,
    instructorName,
    overallPerformance,
    strengths,
    weaknesses,
    recommendations,
  } = localSummary;

  // Get user from AuthContext
  const { user } = useContext(AuthContext);

  // State for institute info
  const [instituteInfo, setInstituteInfo] = useState({
    logoURL: "",
    instituteName: "",
  });

  // State for class info
  const [classInfo, setClassInfo] = useState(
    classInfoProp || {
      level: "",
      className: "",
    }
  );

  // Handle changes in the summary fields
  const handleChange = (field, value) => {
    setLocalSummary((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleListItemChange = (field, index, value) => {
    setLocalSummary((prev) => {
      const updatedList = [...prev[field]];
      updatedList[index] = value;
      return {
        ...prev,
        [field]: updatedList,
      };
    });
  };
  const addListItem = (field) => {
    setLocalSummary((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeListItem = (field, index) => {
    setLocalSummary((prev) => {
      const updatedList = [...prev[field]];
      updatedList.splice(index, 1);
      return {
        ...prev,
        [field]: updatedList,
      };
    });
  };

  // Sync local summary with the initial summary prop from the parent component
  useEffect(() => {
    setLocalSummary(summary);
  }, [summary]);

  // Execute the save function when onSaveSummary === true i.e. when the user clicks the save button in the parent component
  useEffect(() => {
    if (onSaveSummary) {
      handleSaveSummary(localSummary);
    }
  }, [onSaveSummary, handleSaveSummary, localSummary]);

  // Fetch institute data
  useEffect(() => {
    if (instituteInfoProp) {
      setInstituteInfo({
        logoURL: instituteInfoProp.logoURL || "https://res.cloudinary.com/dfdsncmih/image/upload/v1759219779/grademe_ai_logo_sjzbso.png",
        instituteName:
          instituteInfoProp.name ||
          instituteInfoProp.instituteName ||
          "GradeMe AI",
      });
      return;
    }

    const fetchInstitute = async () => {
      if (user?.instituteId && serverUrl) {
        try {
          const res = await fetchWithAuth(
            `${serverUrl}/api/institute/get/${user.instituteId}`,
            { credentials: "include" }
          );
          if (res.ok) {
            const data = await res.json();
            setInstituteInfo({
              logoURL: data.logoURL || "/imgs/logo.svg",
              instituteName: data.name || "GradeMe AI",
            });
          }
        } catch (err) {
          // handle error if needed
        }
      }
    };
    fetchInstitute();
  }, [user?.instituteId, instituteInfoProp]);

  useEffect(() => {
    if (classInfoProp) {
      setClassInfo(classInfoProp);
      return;
    }
    const fetchClass = async () => {
      if (serverUrl && classId) {
        try {
          const res = await fetchWithAuth(
            `${serverUrl}/api/class/get/${classId}`,
            { credentials: "include" }
          );
          if (res.ok) {
            const data = await res.json();
            setClassInfo({
              level: data.level || "",
              className: data.name || data.className || "",
            });
          }
        } catch (err) {
          // handle error if needed
        }
      }
    };
    fetchClass();
  }, [classId, classInfoProp]);

  const dateOptions = {
    year: "numeric",
    month: "short", // e.g., Jan, Feb
    day: "numeric",
  };

  return (
    <div className="performance-report-container">
      <header>
        <div className="institute-header">
          {/* Institute logo and name */}
          {instituteInfo.logoURL && (
            <img
              src={instituteInfo.logoURL}
              alt="Institute Logo"
              style={{ maxHeight: 70, marginBottom: 8 }}
            />
          )}
          {instituteInfo.instituteName && (
            <h1 style={{ margin: 0 }}>{instituteInfo.instituteName}</h1>
          )}
        </div>
        <h2>Performance Report</h2>
        <div className="meta">
          <h3>
            Level: {classInfo.level} | Class : {classInfo.name || classInfo.className} | Test
            Name: {testName}{" "}
          </h3>
          <h4>Student Name: {studentName}</h4>
          {localSummary.createdAt && (
            <h4>
              Generated on:{" "}
              {new Date(localSummary.createdAt).toLocaleString(
                undefined,
                dateOptions
              )}
            </h4>
          )}
          {localSummary.updatedAt &&
            localSummary.createdAt !== localSummary.updatedAt && (
              <p>
                Last updated on:{" "}
                {new Date(localSummary.updatedAt).toLocaleString(
                  undefined,
                  dateOptions
                )}
              </p>
            )}
        </div>
      </header>

      <section>
        <h2>Overall Performance</h2>
        {isSummaryEditing ? (
          <textarea
            value={overallPerformance}
            onChange={(e) => handleChange("overallPerformance", e.target.value)}
          />
        ) : (
          <div className="overall-performance-text">
            <ReactMarkdown
              remarkPlugins={REMARK_PLUGINS}
              rehypePlugins={REHYPE_PLUGINS}
            >
              {formatLatexText(overallPerformance)}
            </ReactMarkdown>
          </div>
        )}
      </section>

      <section>
        <h2>Strengths</h2>
        {isSummaryEditing ? (
          <div className="editable-list">
            {strengths.map((item, index) => (
              <div key={index} className="list-item-edit">
                <textarea
                  value={item}
                  onChange={(e) =>
                    handleListItemChange("strengths", index, e.target.value)
                  }
                  placeholder={`Strength ${index + 1}`}
                />
                <button
                  type="button"
                  title="Remove Item"
                  onClick={() => removeListItem("strengths", index)}
                >
                  −
                </button>
              </div>
            ))}
            <button
              type="button"
              title="Add Item"
              onClick={() => addListItem("strengths")}
            >
              + Add
            </button>
          </div>
        ) : (
          <ul>
            {strengths.map((item, index) => (
              <li key={index}>
                <ReactMarkdown
                  remarkPlugins={REMARK_PLUGINS}
                  rehypePlugins={REHYPE_PLUGINS}
                  components={SPAN_COMPONENTS}
                >
                  {formatLatexText(item)}
                </ReactMarkdown>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2>Areas for Improvement</h2>{" "}
        {/* Display "Areas for Improvement" for weaknesses */}
        {isSummaryEditing ? (
          <div className="editable-list">
            {weaknesses.map((item, index) => (
              <div key={index} className="list-item-edit">
                <textarea
                  value={item}
                  onChange={(e) =>
                    handleListItemChange("weaknesses", index, e.target.value)
                  }
                  placeholder={`Weakness ${index + 1}`}
                />
                <button
                  type="button"
                  title="Remove Item"
                  onClick={() => removeListItem("weaknesses", index)}
                >
                  −
                </button>
              </div>
            ))}
            <button
              type="button"
              title="Add Item"
              onClick={() => addListItem("weaknesses")}
            >
              + Add
            </button>
          </div>
        ) : (
          <ul>
            {weaknesses.map((item, index) => (
              <li key={index}>
                <ReactMarkdown
                  remarkPlugins={REMARK_PLUGINS}
                  rehypePlugins={REHYPE_PLUGINS}
                  components={SPAN_COMPONENTS}
                >
                  {formatLatexText(item)}
                </ReactMarkdown>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2>Recommendations</h2>
        {isSummaryEditing ? (
          <div className="editable-list">
            {recommendations.map((item, index) => (
              <div key={index} className="list-item-edit">
                <textarea
                  value={item}
                  onChange={(e) =>
                    handleListItemChange(
                      "recommendations",
                      index,
                      e.target.value
                    )
                  }
                  placeholder={`Recommendation ${index + 1}`}
                />
                <button
                  type="button"
                  title="Remove Item"
                  onClick={() => removeListItem("recommendations", index)}
                >
                  −
                </button>
              </div>
            ))}
            <button
              type="button"
              title="Add Item"
              onClick={() => addListItem("recommendations")}
            >
              + Add
            </button>
          </div>
        ) : (
          <ul>
            {recommendations.map((item, index) => (
              <li key={index}>
                <ReactMarkdown
                  remarkPlugins={REMARK_PLUGINS}
                  rehypePlugins={REHYPE_PLUGINS}
                  components={SPAN_COMPONENTS}
                >
                  {formatLatexText(item)}
                </ReactMarkdown>
              </li>
            ))}
          </ul>
        )}
      </section>

      <footer>{instructorName && <p>Evaluator: {instructorName}</p>}</footer>
    </div>
  );
};

export default PerformanceReport;
